import { gettext } from "../i18n";
import { nativeAppBridge } from "utils/nativeAppBridge";

const MOBILE_SESSION_TOKEN_KEY = "mgSessionToken";

const ERROR_MESSAGES: Record<number, string> = {
  400: "Bad request",
  401: "Unauthorized",
  403: "Forbidden",
  404: "Not found",
  500: "Internal server error",
  502: "Bad gateway",
  503: "Service unavailable",
  504: "Gateway timeout",
};

const getErrorMessageForCode = (code: number): string =>
  gettext(ERROR_MESSAGES[code] || "Unspecified server error");

function extractCustomError(htmlResponse: string): string {
  const match = htmlResponse.match(/\|\|MESSAGE\|\|(.*?)\|\|\/MESSAGE\|\|/);
  return match ? match[1] : "";
}

interface RequestOpts {
  method?: "GET" | "POST" | "PUT" | "DELETE";
  username?: string;
  password?: string;
  data?: Record<string, any>;
  multipart?: boolean;
}

export class ApiError extends Error {
  // The JSON body of the error response, e.g. { step_up_required: true }
  data: Record<string, any>;

  constructor(message: string, data: Record<string, any> = {}) {
    super(message);
    this.data = data;
  }
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl.replace(/\/*$/, "");
  }

  async request(
    path: string,
    opts: RequestOpts = {},
    refreshJWT: boolean = true,
  ): Promise<Record<string, any>> {
    const response = await this.requestRaw(path, opts, refreshJWT);
    if (!response.ok) {
      const statusCode = response.status;
      let message = await response.text();
      let jsonError = false;
      let errorData: Record<string, any> = {};
      try {
        const jsonData = JSON.parse(message);
        if (jsonData.error) {
          message = jsonData.error;
          jsonError = true;
          errorData = jsonData;
        }
      } catch (e) {}
      if (statusCode === 403 && errorData.restriction) {
        // The session may now only e.g. set up MFA: reload to show only that
        this.discardAuthJWT();
        window.location.reload();
      }
      if (statusCode === 401) {
        // The cached JWT may be rejected before it expires (e.g. after the signing key is replaced), so drop it
        // for the reload to fetch a new one with the session cookie
        this.discardAuthJWT();
        window.location.reload();
      }
      if (!jsonError) {
        message = extractCustomError(message);
        if (!message) {
          message = `${statusCode}: ${getErrorMessageForCode(statusCode)}`;
        }
        console.error(`API error ${statusCode}: `, message);
      }
      throw new ApiError(message, errorData);
    }
    return await response.json();
  }

  discardAuthJWT() {
    window.localStorage.removeItem("authJWT");
  }

  async getAuthJWT(refreshJWT: boolean = true) {
    let authJWT = JSON.parse(window.localStorage.getItem("authJWT") ?? "{}");
    const expiryThreshold = 2 * 60;
    const currentTimestamp = new Date().getTime() / 1000;

    if (
      authJWT.token !== undefined &&
      authJWT.expiresAt !== null &&
      authJWT.expiresAt - expiryThreshold > currentTimestamp
    ) {
      return authJWT;
    }

    if (!refreshJWT) {
      return {};
    }

    const url = this.getApiUrl("/auth/token/reissue");
    const fetchOpts: RequestInit = {
      method: "POST",
      credentials: "include",
    };

    if (nativeAppBridge.isNativeConnected) {
      const sessionToken = window.localStorage.getItem(
        MOBILE_SESSION_TOKEN_KEY,
      );
      if (sessionToken) {
        fetchOpts.headers = { "Content-Type": "application/json" };
        fetchOpts.body = JSON.stringify({ long_term_token: sessionToken });
      }
    }

    const response = await fetch(url, fetchOpts);

    if (!response.ok && response.status === 401) {
      authJWT = {
        token: null,
        expiresAt: null,
      };
      window.localStorage.removeItem("authJWT");
      return authJWT;
    }
    const data = await response.json();
    const previousRestriction = authJWT.token
      ? (authJWT.restriction ?? null)
      : null;

    authJWT = {
      token: data.token,
      expiresAt: data.expires_at,
      // What the session may only do for now, e.g. set up MFA (mg.auth.restrictions)
      restriction: data.restriction ?? null,
    };

    window.localStorage.setItem("authJWT", JSON.stringify(authJWT));
    if (!previousRestriction && authJWT.restriction) {
      // e.g. an organization started requiring MFA, or the session is now used from an unusual place
      window.location.reload();
    }
    return authJWT;
  }

  async requestRaw(
    path: string,
    opts: RequestOpts = {},
    refreshJWT: boolean = true,
  ): Promise<Response> {
    let { data, method = "GET", multipart } = opts;
    const url = this.getApiUrl(path);

    const authJWT = await this.getAuthJWT(refreshJWT);
    const headers = new Headers();
    if (!multipart) {
      headers.set("Content-Type", "application/json");
    }
    if (authJWT.token) {
      headers.set("Authorization", `Bearer ${authJWT.token}`);
    }
    if (nativeAppBridge.isNativeConnected) {
      headers.set("X-MG-Client", "mobile");
    }

    const fetchOpts: RequestInit = {
      method,
      credentials: "include",
      mode: "cors",
      headers,
    };
    if (data) {
      if (multipart) {
        fetchOpts.body = new FormData();
        for (const [key, value] of Object.entries(data)) {
          fetchOpts.body.append(key, value);
        }
      } else {
        fetchOpts.body = JSON.stringify(data);
      }
    }
    return await fetch(url, fetchOpts);
  }

  getApiUrl(path: string) {
    if (path.charAt(0) !== "/") {
      path = "/" + path;
    }
    return `${this.baseUrl}${path}`;
  }
}
export default ApiClient;
